import { Helmet } from "react-helmet-async";
import { useLocation, useParams } from 'react-router-dom';
import { SUPPORTED_LANGS } from '../utils/languageStorage';
import { getPathWithoutLang } from '../utils/navigation';

type SEOProps = {
  title?: string;
  description?: string;
  url?: string;
  imageUrl?: string;
  type?: "website" | "article" | "product";
  schema?: object;
  noIndex?: boolean;
};

const defaultTitle = "Sarvalok | An AI Gateway to Sanātana Dharma & Indic Scriptures";
const defaultDescription =
  "The essential AI tool for Indology, research, and spiritual study. Sarvalok offers source-grounded analysis of the Vedas, Upanishads, and Sanātana Dharma texts.";
const siteUrl = "https://sarvalok.com";
const defaultOgImage = `${siteUrl}/logo.png`;

// URL language codes intentionally stay stable (/cn, /zh), while hreflang
// uses valid Google/BCP-47 language tags. Keep this in sync with the sitemap generator.
const LANG_HREFLANG: Record<string, string> = {
  en: "en",
  cn: "zh-CN",
  zh: "zh-TW",
  de: "de",
  es: "es",
  pl: "pl",
  hi: "hi",
  ta: "ta",
  te: "te",
  ne: "ne",
  he: "he",
  fr: "fr",
  ja: "ja",
  pt: "pt",
  id: "id",
  ru: "ru",
  nl: "nl",
};

const OG_LOCALE: Record<string, string> = {
  en: "en_US",
  cn: "zh_CN",
  zh: "zh_TW",
  de: "de_DE",
  es: "es_ES",
  pl: "pl_PL",
  hi: "hi_IN",
  ta: "ta_IN",
  te: "te_IN",
  ne: "ne_NP",
  he: "he_IL",
  fr: "fr_FR",
  ja: "ja_JP",
  pt: "pt_BR",
  id: "id_ID",
  ru: "ru_RU",
  nl: "nl_NL",
};

const isSupportedLang = (value?: string): value is string =>
  Boolean(value && SUPPORTED_LANGS.includes(value));

const normalizePath = (path: string) => {
  if (!path || path === "/") return "/";
  const withSlash = path.startsWith("/") ? path : `/${path}`;
  return withSlash.length > 1 ? withSlash.replace(/\/+$/, "") : withSlash;
};

const isAbsoluteUrl = (value: string) => /^https?:\/\//i.test(value);

const hasLanguagePrefix = (path: string) => {
  const firstSegment = normalizePath(path).split("/")[1];
  return isSupportedLang(firstSegment);
};

const localizePath = (path: string, lang: string) => {
  const normalized = normalizePath(path);
  if (hasLanguagePrefix(normalized)) return normalized;
  if (normalized === "/") return `/${lang}/`;
  return `/${lang}${normalized}`;
};

const getPathForAlternates = (targetUrl: string | undefined, fallbackPath: string) => {
  if (!targetUrl) return normalizePath(getPathWithoutLang(fallbackPath));
  if (isAbsoluteUrl(targetUrl)) {
    try {
      return normalizePath(getPathWithoutLang(new URL(targetUrl).pathname));
    } catch {
      return normalizePath(getPathWithoutLang(fallbackPath));
    }
  }

  return normalizePath(getPathWithoutLang(targetUrl));
};

const resolveCanonicalUrl = (targetUrl: string | undefined, lang: string, alternateBasePath: string) => {
  if (!targetUrl) return `${siteUrl}${localizePath(alternateBasePath, lang)}`;
  if (isAbsoluteUrl(targetUrl)) return targetUrl;
  return `${siteUrl}${localizePath(targetUrl, lang)}`;
};

export const SEO = ({
  title,
  description,
  url,
  imageUrl,
  type = "website",
  schema,
  noIndex = false,
}: SEOProps) => {
  const { pathname } = useLocation();
  const { lang } = useParams();
  
  const currentLang = isSupportedLang(lang) ? lang : 'en';
  const alternateBasePath = getPathForAlternates(url, pathname);
  
  const pageTitle = title ? `${title} | Sarvalok` : defaultTitle;
  const pageDescription = description || defaultDescription;
  const pageUrl = resolveCanonicalUrl(url, currentLang, alternateBasePath);
  const pageImage = imageUrl || defaultOgImage;
  const htmlLang = LANG_HREFLANG[currentLang] || currentLang;

  return (
    <Helmet>
      {/* Language attribute */}
      <html lang={htmlLang} />
      
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="theme-color" content="#FF6B00" />

      <link rel="canonical" href={pageUrl} />
      
      {/* Hreflang tags for all supported languages. The URL code may differ from the hreflang code. */}
      {SUPPORTED_LANGS.map((lng) => (
        <link
          key={lng}
          rel="alternate"
          href={`${siteUrl}${localizePath(alternateBasePath, lng)}`}
          hrefLang={LANG_HREFLANG[lng] || lng}
        />
      ))}
      <link rel="alternate" href={`${siteUrl}${localizePath(alternateBasePath, "en")}`} hrefLang="x-default" />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:image" content={pageImage} />
      <meta property="og:site_name" content="Sarvalok" />
      <meta property="og:locale" content={OG_LOCALE[currentLang] || "en_US"} />
      
      {/* Additional language-specific Open Graph tags */}
      {currentLang === 'hi' && (
        <>
          <meta property="og:locale:alternate" content="en_US" />
          <meta property="og:locale:alternate" content="ta_IN" />
        </>
      )}
      {currentLang === 'ta' && (
        <>
          <meta property="og:locale:alternate" content="en_US" />
          <meta property="og:locale:alternate" content="hi_IN" />
        </>
      )}
      {currentLang === 'en' && (
        <>
          <meta property="og:locale:alternate" content="hi_IN" />
          <meta property="og:locale:alternate" content="ta_IN" />
        </>
      )}
      {currentLang === 'cn' && (
        <>
          <meta property="og:locale:alternate" content="zh_TW" />
          <meta property="og:locale:alternate" content="zh_HK" />
          <meta property="og:locale:alternate" content="en_US" />
        </>
      )}
      {currentLang === 'zh' && (
        <>
          <meta property="og:locale:alternate" content="zh_CN" />
          <meta property="og:locale:alternate" content="zh_HK" />
          <meta property="og:locale:alternate" content="zh_MO" />
          <meta property="og:locale:alternate" content="en_US" />
        </>
      )}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={pageImage} />

      {/* Fonts are now self-hosted via @fontsource — no Google Fonts preconnect needed */}

      {/* Noindex for restricted pages */}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* JSON-LD Schema */}
      {schema && (
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      )}
    </Helmet>
  );
};
